import React, { createContext, useContext, useState, useCallback } from "react";
import { BubbleData } from "./types";

export interface BubbleContextType {
  activeBubble: BubbleData | null;
  closingBubble: BubbleData | null;
  setActiveBubble: (bubble: BubbleData | null) => void;
  triggerBubble: (content: React.ReactNode, side: "left" | "right") => void;
  closeBubble: () => void;
  handleAnimationEnd: () => void;
  isExpanded: boolean;
  expandBubble: () => void;
  collapseBubble: () => void;
}

const BubbleContext = createContext<BubbleContextType | undefined>(undefined);

export interface BubbleProviderProps {
  children: React.ReactNode;
  verticalRange: { min: number; max: number };
}

export const BubbleProvider: React.FC<BubbleProviderProps> = ({
  children,
  verticalRange,
}) => {
  const [activeBubble, setActiveBubbleState] = useState<BubbleData | null>(null);
  const [closingBubble, setClosingBubble] = useState<BubbleData | null>(null);
  const [pendingBubble, setPendingBubble] = useState<BubbleData | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const setActiveBubble = useCallback(
    (bubble: BubbleData | null) => {
      if (bubble) {
        if (activeBubble) {
          setClosingBubble(activeBubble);
          setActiveBubbleState(null);
          setPendingBubble(bubble);
        } else {
          setActiveBubbleState(bubble);
        }
      } else {
        if (activeBubble) {
          setClosingBubble(activeBubble);
          setActiveBubbleState(null);
          setIsExpanded(false);
        }
      }
    },
    [activeBubble],
  );

  const triggerBubble = useCallback(
    (content: React.ReactNode, side: "left" | "right") => {
      const verticalPosition =
        verticalRange.min +
        Math.random() * (verticalRange.max - verticalRange.min);

      setActiveBubble({
        id: Date.now(),
        side,
        verticalPosition,
        content,
      });
    },
    [setActiveBubble, verticalRange.min, verticalRange.max],
  );

  const closeBubble = useCallback(() => {
    setIsExpanded(false);
    setActiveBubble(null);
  }, [setActiveBubble]);

  const expandBubble = useCallback(() => {
    if (activeBubble) {
      setIsExpanded(true);
    }
  }, [activeBubble]);

  const collapseBubble = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    setClosingBubble(null);
    if (pendingBubble) {
      setActiveBubbleState(pendingBubble);
      setPendingBubble(null);
    }
  }, [pendingBubble]);

  return (
    <BubbleContext.Provider
      value={{
        activeBubble,
        closingBubble,
        setActiveBubble,
        triggerBubble,
        closeBubble,
        handleAnimationEnd,
        isExpanded,
        expandBubble,
        collapseBubble,
      }}
    >
      {children}
    </BubbleContext.Provider>
  );
};

export const useBubble = (): BubbleContextType => {
  const context = useContext(BubbleContext);
  if (!context) {
    throw new Error("useBubble must be used within BubbleProvider");
  }
  return context;
};
